import z from 'zod'
import { sql } from 'drizzle-orm'
import {
  pgTable,
  serial,
  integer,
  text,
  timestamp,
  boolean,
  index,
  jsonb,
} from 'drizzle-orm/pg-core'
import { createSelectSchema } from 'drizzle-zod'
import { pgBrand } from './brand'
import { pgUser } from './user'
import { pgMoodboard } from './moodboard'

export const DAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const
export type DayOfWeek = (typeof DAY_NAMES)[number]
export const zDayOfWeek = z.enum(DAY_NAMES)

// YouTube is intentionally excluded: it's a video-first surface and CASSI
// can't generate video content yet. Re-add once we can.
export const AUTOMATION_PLATFORMS = ['linkedin', 'instagram', 'tiktok', 'facebook'] as const
export type AutomationPlatform = (typeof AUTOMATION_PLATFORMS)[number]
export const zAutomationPlatform = z.enum(AUTOMATION_PLATFORMS)

// One remembered reminder topic. `format` is the slate angle it was written for
// (null for entries backfilled from the old topic-only column); `at` orders
// entries when a brand's automations are merged into one history; `sources` are the
// research findings (sourceRefs) the post was built on, so later research skips them.
export type RecentSuggestion = {
  topic: string
  format: string | null
  at: string | null
  sources?: string[]
}

export const pgAutomation = pgTable(
  'automation',
  {
    id: serial('id').primaryKey(),
    userId: integer('user_id')
      .notNull()
      .references(() => pgUser.id, { onDelete: 'cascade' }),
    brandId: integer('brand_id')
      .notNull()
      .references(() => pgBrand.id, { onDelete: 'cascade' }),
    // Content source for this automation; resolved in generateReminders.ts.
    moodboardId: integer('moodboard_id')
      .notNull()
      .references(() => pgMoodboard.id, { onDelete: 'cascade' }),

    dayOfWeek: text('day_of_week').$type<DayOfWeek>().notNull(),
    platforms: text('platforms').array().$type<AutomationPlatform[]>().notNull(),

    sendReminders: boolean('send_reminders').notNull().default(true),
    // Set by the dispatcher when it claims today's run (the per-day dedupe).
    lastTriggeredAt: timestamp('last_triggered_at'),
    // Set atomically by the worker as it starts. Lambda may deliver the same async event
    // twice, even concurrently; only the worker that wins this claim runs. Reclaimable once
    // older than the Lambda timeout, when its invocation can no longer exist.
    reminderStartedAt: timestamp('reminder_started_at'),
    // Set by the worker right after the reminder email goes out. A retried worker skips when
    // this is at or after the dispatcher's claim, so a reminder is not emailed twice.
    reminderSentAt: timestamp('reminder_sent_at'),

    // Deprecated: superseded by recentSuggestions, which migration 0066 backfilled
    // from it. No longer read or written; drop once that deploy is live.
    recentTopics: text('recent_topics')
      .array()
      .$type<string[]>()
      .notNull()
      .default(sql`'{}'`),

    // Short-term memory for the reminder: the last ~10 topics with their angle.
    // Read brand-wide (all of a brand's automations together) so the Tuesday and
    // Friday reminders don't repeat each other.
    recentSuggestions: jsonb('recent_suggestions')
      .$type<RecentSuggestion[]>()
      .notNull()
      .default([]),

    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (table) => [index('automation_brand_day_idx').on(table.brandId, table.dayOfWeek)],
)

export const zAutomationPlatforms = z.array(zAutomationPlatform).min(1)

export const zAutomation = createSelectSchema(pgAutomation).extend({
  dayOfWeek: zDayOfWeek,
  platforms: zAutomationPlatforms,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lastTriggeredAt: z.coerce.date().nullable(),
})
export type Automation = z.infer<typeof zAutomation>
